/**-----------------------------------------------------------------------------------------
* Copyright © 2021 Progress Software Corporation. All rights reserved.
* Licensed under commercial license. See LICENSE.md in the project root for more information
*-------------------------------------------------------------------------------------------*/
"use strict";
/* tslint:disable:member-ordering */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var core_1 = require("@angular/core");
var forms_1 = require("@angular/forms");
var kendo_licensing_1 = require("@progress/kendo-licensing");
var package_metadata_1 = require("../package-metadata");
var searchbar_component_1 = require("../common/searchbar.component");
var item_template_directive_1 = require("../common/templates/item-template.directive");
var header_template_directive_1 = require("../common/templates/header-template.directive");
var footer_template_directive_1 = require("../common/templates/footer-template.directive");
var group_template_directive_1 = require("../common/templates/group-template.directive");
var fixed_group_template_directive_1 = require("../common/templates/fixed-group-template.directive");
var no_data_template_directive_1 = require("../common/templates/no-data-template.directive");
var selection_service_1 = require("../common/selection/selection.service");
var navigation_service_1 = require("../common/navigation/navigation.service");
var disabled_items_service_1 = require("../common/disabled-items/disabled-items.service");
var rxjs_1 = require("rxjs");
var operators_1 = require("rxjs/operators");
var kendo_angular_common_1 = require("@progress/kendo-angular-common");
var util_1 = require("../common/util");
var navigation_action_1 = require("../common/navigation/navigation-action");
var kendo_angular_common_2 = require("@progress/kendo-angular-common");
var preventable_event_1 = require("../common/models/preventable-event");
var kendo_angular_l10n_1 = require("@progress/kendo-angular-l10n");
var kendo_angular_popup_1 = require("@progress/kendo-angular-popup");
var touch_enabled_1 = require("../common/constants/touch-enabled");
var error_messages_1 = require("../common/constants/error-messages");
var filterable_component_1 = require("../common/filtering/filterable-component");
var data_service_1 = require("../common/data.service");
var list_component_1 = require("../common/list.component");
var virtualization_settings_1 = require("../common/models/virtualization-settings");
var kendo_common_1 = require("@progress/kendo-common");
/**
 * @hidden
 */
exports.COMBOBOX_VALUE_ACCESSOR = {
    multi: true,
    provide: forms_1.NG_VALUE_ACCESSOR,
    useExisting: core_1.forwardRef(function () { return ComboBoxComponent; })
};
/**
 * Represents the [Kendo UI ComboBox component for Angular]({% slug overview_combobox %}).
 *
 * @example
 * ```ts
 * _@Component({
 * selector: 'my-app',
 * template: `
 *  <kendo-combobox [data]="listItems">
 *  </kendo-combobox>
 * `
 * })
 * class AppComponent {
 *   public listItems: Array<string> = ["Item 1", "Item 2", "Item 3", "Item 4"];
 * }
 * ```
 */
var ComboBoxComponent = /** @class */ (function () {
    function ComboBoxComponent(wrapper, localization, popupService, selectionService, navigationService, disabledItemsService, dataService, zone, cdr, renderer, touchEnabled) {
        this.wrapper = wrapper;
        this.localization = localization;
        this.popupService = popupService;
        this.selectionService = selectionService;
        this.navigationService = navigationService;
        this.disabledItemsService = disabledItemsService;
        this.dataService = dataService;
        this.zone = zone;
        this.cdr = cdr;
        this.renderer = renderer;
        this.touchEnabled = touchEnabled;
        this.selected = [];
        /**
         * @hidden
         */
        this.focusableId = "k-" + util_1.guid();
        /**
         * Specifies whether the ComboBox allows user-defined values that are not present in the dataset
         * ([more information and examples]({% slug custom_values_combobox %})).
         * Defaults to `false`.
         */
        this.allowCustom = false;
        /**
         * A user-defined callback which returns normalized custom values.
         * Typically used when the data items are different from type `string`.
         * @param { Any } value - The custom value defined by the user.
         * @returns { Any }
         *
         * @example
         * ```ts
         * import { map } from 'rxjs/operators';
         *
         * _@Component({
         * selector: 'my-app',
         * template: `
         *   <kendo-combobox
         *       [allowCustom]="true"
         *       [data]="listItems"
         *       textField="text"
         *       valueField="value"
         *       [valueNormalizer]="valueNormalizer"
         *       (valueChange)="onValueChange($event)"
         *   >
         *   </kendo-combobox>
         * `
         * })
         *
         * class AppComponent {
         *   public listItems: Array<{ text: string, value: number }> = [
         *       { text: "Small", value: 1 },
         *       { text: "Medium", value: 2 },
         *       { text: "Large", value: 3 }
         *   ];
         *
         *   public onValueChange(value) {
         *       console.log("valueChange : ", value);
         *   }
         *
         *   public valueNormalizer = (text$: Observable<string>) => text$.pipe(map((text: string) => {
         *      return { ProductID: null, ProductName: text };
         *   }));
         *
         * }
         * ```
         */
        this.valueNormalizer = function (text) {
            return text.pipe(operators_1.map(function (userInput) { return userInput; }));
        };
        /**
         * The hint that is displayed when the component is empty.
         *
         */
        this.placeholder = "";
        /**
         * Sets the height of the suggestions list. By default, `listHeight` is 200px.
         *
         * > The `listHeight` property affects only the list of suggestions and not the whole popup container.
         * > To set the height of the popup container, use `popupSettings.height`.
         */
        this.listHeight = 200;
        /**
         * Enables the auto-completion of the text based on the first data item.
         */
        this.suggest = false;
        /**
         * If set to `true`, renders a button on hovering over the component.
         * Clicking this button resets the value of the component to `undefined` and triggers the `change` event.
         */
        this.clearButton = true;
        /**
         * Sets the disabled state of the component.
         */
        this.disabled = false;
        /**
         * Sets the read-only state of the component.
         */
        this.readonly = false;
        /**
         * Specifies the [`tabindex`](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex) of the component.
         */
        this.tabindex = 0;
        /**
         * Enables the [filtering]({% slug filtering_combobox %}) functionality.
         * If set to `true`, the component emits the `filterChange` event.
         */
        this.filterable = false;
        /**
         * Fires each time the value is changed&mdash;
         * when the component is blurred or the value is cleared through the **Clear** button
         * ([see example]({% slug overview_combobox %}#toc-events)).
         * When the value of the component is programmatically changed to `ngModel` or `formControl`
         * through its API or form binding, the `valueChange` event is not triggered because it
         * might cause a mix-up with the built-in `valueChange` mechanisms of the `ngModel` or `formControl` bindings.
         */
        this.valueChange = new core_1.EventEmitter();
        /**
         * Fires each time an item selection is changed
         * ([see example]({% slug overview_combobox %}#toc-events)).
         */
        this.selectionChange = new core_1.EventEmitter();
        /**
         * Fires each time the user types in the input field.
         * You can filter the source based on the passed filtration value
         * ([see example]({% slug overview_combobox %}#toc-events)).
         */
        this.filterChange = new core_1.EventEmitter();
        /**
         * Fires each time the popup is about to open.
         * This event is preventable. If you cancel it, the popup will remain closed.
         */
        this.open = new core_1.EventEmitter();
        /**
         * Fires after the popup has been opened.
         */
        this.opened = new core_1.EventEmitter();
        /**
         * Fires each time the popup is about to close.
         * This event is preventable. If you cancel it, the popup will remain open.
         */
        this.close = new core_1.EventEmitter();
        /**
         * Fires after the popup has been closed.
         */
        this.closed = new core_1.EventEmitter();
        /**
         * Fires each time the user focuses the ComboBox.
         */
        this.onFocus = new core_1.EventEmitter();
        /**
         * Fires each time the ComboBox gets blurred.
         */
        this.onBlur = new core_1.EventEmitter();
        this.widgetClasses = true;
        this._isFocused = false;
        this.listBoxId = util_1.guid();
        this.optionPrefix = util_1.guid();
        this.onChangeCallback = function (_) { };
        this.onTouchedCallback = function (_) { };
        /**
         * Used for the default virtualization settings config.
         */
        this.defaultVirtualItemHeight = 28;
        /**
         * Used for the default virtualization settings config.
         */
        this.defaultVirtualPageSize = 50;
        this._filtering = false;
        this._text = '';
        this.filterText = '';
        this._open = false;
        this._popupSettings = { animate: true };
        this.popupMouseDownHandler = function (event) { return event.preventDefault(); };
        this.customValueSubject = new rxjs_1.Subject();
        this.valueSubject = new rxjs_1.Subject();
        this.clearValueSubject = new rxjs_1.Subject();
        this.subs = new rxjs_1.Subscription();
        this._size = 'medium';
        this._rounded = 'medium';
        this._fillMode = 'solid';
        kendo_licensing_1.validatePackage(package_metadata_1.packageMetadata);
        this.direction = localization.rtl ? 'rtl' : 'ltr';
        this.data = [];
    }
    ComboBoxComponent_1 = ComboBoxComponent;
    Object.defineProperty(ComboBoxComponent.prototype, "text", {
        get: function () {
            return this._text;
        },
        set: function (text) {
            this._text = util_1.isPresent(text) ? text.toString() : "";
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.togglePopup = function (open) {
        var isDisabled = this.disabled || this.readonly;
        var sameState = this.isOpen === open;
        if (isDisabled || sameState) {
            return;
        }
        var isDefaultPrevented = this.triggerPopupEvents(open);
        if (!isDefaultPrevented) {
            this._toggle(open);
        }
    };
    Object.defineProperty(ComboBoxComponent.prototype, "activeDescendant", {
        get: function () {
            if (!this.isOpen || !util_1.isPresent(this.selectionService.focused) || this.selectionService.focused === -1) {
                return null;
            }
            var dataItem = this.dataService.itemAt(this.selectionService.focused);
            return this.optionPrefix + "-" + (dataItem ? util_1.getter(dataItem, this.valueField) : "");
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "noDataLabel", {
        get: function () {
            if (this.data.length === 0) {
                return this.messageFor('noDataText');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "appendTo", {
        get: function () {
            var appendTo = this.popupSettings.appendTo;
            if (!appendTo || appendTo === 'root') {
                return undefined;
            }
            return appendTo === 'component' ? this.container : appendTo;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "data", {
        get: function () {
            var virtual = this.virtual;
            if (virtual) {
                var start = virtual.skip || 0;
                var end = start + virtual.pageSize;
                // Use length instead of itemsCount because of the grouping.
                virtual.total = this.dataService.data.length;
                return this.dataService.data.slice(start, end);
            }
            return this.dataService.data;
        },
        /**
         * Sets the data of the ComboBox.
         *
         * > The data has to be provided in an array-like list.
         */
        set: function (data) {
            this.dataService.data = data || [];
            if (this.virtual) {
                this.virtual.skip = 0;
            }
            this.setState();
            if (this._filtering) {
                var queryAndDataPresent = this.text.length > 0 && this.dataService.itemsCount > 0;
                var index = queryAndDataPresent ? this.firstFocusableIndex(0) : -1;
                this.selectionService.focused = index;
            }
            if (this.suggest && this.dataService.itemsCount && this.text) {
                this.suggestedText = util_1.getter(this.dataService.itemAt(0), this.textField);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "value", {
        get: function () {
            return this._value;
        },
        /**
         * Sets the value of the ComboBox.
         * It can either be of the primitive (string, numbers) or of the complex (objects) type.
         * To define the type, use the `valuePrimitive` option.
         *
         * > All selected values which are not present in the dataset are considered custom values.
         * > When the `Enter` key is pressed or the component loses focus, custom values get dismissed unless `allowCustom` is set to `true`.
         */
        set: function (newValue) {
            this._value = newValue;
            this.setState();
            this.cdr.markForCheck();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "valuePrimitive", {
        get: function () {
            if (!util_1.isPresent(this._valuePrimitive)) {
                return !util_1.isPresent(this.valueField);
            }
            return this._valuePrimitive;
        },
        /**
         * Specifies the type of the selected value.
         * If set to `true`, the selected value has to be of the primitive type
         * ([more information and example]({% slug valuebinding_combobox %}#toc-primitive-values-from-object-fields)).
         */
        set: function (isPrimitive) {
            this._valuePrimitive = isPrimitive;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "popupSettings", {
        get: function () {
            return this._popupSettings;
        },
        /**
         * Configures the popup of the ComboBox.
         *
         * The available options are:
         * - `animate: Boolean`&mdash;Controls the popup animation. By default, the open and close animations are enabled.
         * - `width: Number | String`&mdash;Sets the width of the popup container. By default, the width of the host element is used. If set to `auto`, the component automatically adjusts the width of the popup and no item labels are wrapped. The `auto` mode is not supported when virtual scrolling is enabled.
         * - `height: Number`&mdash;Sets the height of the popup container.
         * - `popupClass: String`&mdash;Specifies a list of CSS classes that are used to style the popup.
         * - `appendTo: "root" | "component" | ViewContainerRef`&mdash;Specifies the component to which the popup will be appended.
         */
        set: function (settings) {
            this._popupSettings = Object.assign({ animate: true }, settings);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "itemDisabled", {
        /**
         * Defines a Boolean function that is executed for each data item in the component
         * ([see examples]({% slug disableditems_combobox %})). Determines whether the item will be disabled.
         */
        set: function (fn) {
            if (typeof fn !== 'function') {
                throw new Error("itemDisabled must be a function, but received " + JSON.stringify(fn) + ".");
            }
            this.disabledItemsService.itemDisabled = fn;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "tabIndex", {
        get: function () {
            return this.tabindex;
        },
        /**
         * @hidden
         */
        set: function (tabIndex) {
            this.tabindex = tabIndex;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "virtual", {
        get: function () {
            return this._virtualSettings;
        },
        /**
         * Enables the [virtualization]({% slug virtualization_combobox %}) functionality.
         */
        set: function (settings) {
            this._virtualSettings = virtualization_settings_1.normalizeVirtualizationSettings(settings, {
                itemHeight: this.defaultVirtualItemHeight,
                pageSize: this.defaultVirtualPageSize
            });
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "size", {
        get: function () {
            return this._size;
        },
        /**
         * Sets the size of the component.
         *
         * The possible values are:
         * * `'small'`
         * * `'medium'` (default)
         * * `'large'`
         * * `null`
         *
         */
        set: function (size) {
            this.renderer.removeClass(this.wrapper.nativeElement, util_1.getSizeClass('input', this.size));
            if (size) {
                this.renderer.addClass(this.wrapper.nativeElement, util_1.getSizeClass('input', size));
            }
            this._size = size;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "rounded", {
        get: function () {
            return this._rounded;
        },
        /**
         * Sets the border radius of the component.
         *
         * The possible values are:
         * * `'small'`
         * * `'medium'` (default)
         * * `'large'`
         * * `'full'`
         * * `null`
         *
         */
        set: function (rounded) {
            this.renderer.removeClass(this.wrapper.nativeElement, util_1.getRoundedClass(this.rounded));
            if (rounded) {
                this.renderer.addClass(this.wrapper.nativeElement, util_1.getRoundedClass(rounded));
            }
            this._rounded = rounded;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "fillMode", {
        get: function () {
            return this._fillMode;
        },
        /**
         * Sets the fillMode of the component.
         *
         * The possible values are:
         * * `'flat'`
         * * `'solid'` (default)
         * * `'outline'`
         * * `null`
         *
         */
        set: function (fillMode) {
            this.renderer.removeClass(this.wrapper.nativeElement, util_1.getFillModeClass('input', this.fillMode));
            if (fillMode) {
                this.renderer.addClass(this.wrapper.nativeElement, util_1.getFillModeClass('input', fillMode));
            }
            this._fillMode = fillMode;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "isDisabled", {
        get: function () {
            return this.disabled;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "isLoading", {
        get: function () {
            return this.loading;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "dir", {
        get: function () {
            return this.direction;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "isFocused", {
        get: function () {
            return this._isFocused;
        },
        set: function (value) {
            this.renderer[value ? 'addClass' : 'removeClass'](this.wrapper.nativeElement, "k-focus");
            this._isFocused = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "clearButtonVisiblity", {
        get: function () {
            if (this.touchEnabled) {
                return 'visible';
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "popupWidth", {
        get: function () {
            var wrapperOffsetWidth = 0;
            if (kendo_angular_common_1.isDocumentAvailable()) {
                wrapperOffsetWidth = this.wrapper.nativeElement.offsetWidth;
            }
            var width = this.popupSettings.width || wrapperOffsetWidth;
            var minWidth = isNaN(wrapperOffsetWidth) ? wrapperOffsetWidth : wrapperOffsetWidth + "px";
            var maxWidth = isNaN(width) ? width : width + "px";
            return { min: minWidth, max: maxWidth };
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "popupHeight", {
        get: function () {
            var popupHeight = this.popupSettings.height;
            return util_1.isPresent(popupHeight) ? popupHeight + "px" : 'auto';
        },
        enumerable: true,
        configurable: true
    });
    ComboBoxComponent.prototype.ngOnInit = function () {
        this.renderer.removeAttribute(this.wrapper.nativeElement, 'tabindex');
        this.attachStreams();
        this.createValueStream();
        this.subscribeTouchEvents();
        this.attachSelectClickHandler();
        this.setComponentClasses();
    };
    ComboBoxComponent.prototype.createValueStream = function () {
        var _this = this;
        var valueStream = this.valueSubject.pipe(operators_1.filter(function (candidate) {
            var current = util_1.getter(_this.value, _this.valueField);
            var newValue = util_1.getter(candidate, _this.valueField);
            var newText = util_1.getter(candidate, _this.textField);
            if (!util_1.isPresent(_this.value) && !util_1.isPresent(newValue)) {
                return false;
            }
            if (util_1.isPresent(newText)) {
                newText = newText.toString();
            }
            if (current === newValue && _this.text === newText) {
                _this.clearFilter();
                return false;
            }
            else {
                return true;
            }
        }), operators_1.map(function (candidate) {
            var newValue = util_1.getter(candidate, _this.valueField);
            var newText = util_1.getter(candidate, _this.textField);
            return {
                dataItem: candidate,
                text: newText,
                value: _this.valuePrimitive ? newValue : candidate
            };
        }));
        var customValueStreams = operators_1.partition(function () { return _this.allowCustom; })(this.customValueSubject.pipe(operators_1.throttleTime(300)));
        var allowCustomValueStream = customValueStreams[0].pipe(operators_1.tap(function () {
            _this.loading = true;
            _this.disabled = true;
            _this.cdr.detectChanges();
        }), operators_1.filter(function () {
            var hasChange = _this.text !== util_1.getter(_this.value, _this.valueField);
            _this.loading = hasChange;
            _this.disabled = hasChange;
            if (!hasChange) {
                _this.clearFilter();
            }
            return hasChange;
        }), this.valueNormalizer, operators_1.map(function (normalizedValue) {
            return {
                custom: true,
                dataItem: normalizedValue,
                text: _this.text,
                value: normalizedValue
            };
        }));
        var disableCustomValueStream = customValueStreams[1].pipe(operators_1.map(function () {
            return {
                custom: true,
                dataItem: undefined,
                text: undefined,
                value: undefined
            };
        }));
        var clearValueStream = this.clearValueSubject.pipe(operators_1.map(function () { return ({
            dataItem: undefined,
            text: undefined,
            value: undefined
        }); }));
        if (this.valueSubscription) {
            this.valueSubscription.unsubscribe();
        }
        var merged = rxjs_1.merge(valueStream, allowCustomValueStream, disableCustomValueStream, clearValueStream);
        this.valueSubscription = merged.pipe(operators_1.catchError(function () {
            var selectionChanged = util_1.getter(_this.dataItem, _this.valueField) !== undefined;
            _this.dataItem = undefined;
            _this.value = undefined;
            _this.text = undefined;
            _this.loading = false;
            _this.disabled = false;
            if (selectionChanged) {
                _this.selectionChange.emit(undefined);
            }
            _this.emitValueChange();
            _this.createValueStream();
            return rxjs_1.of(null);
        }))
            .subscribe(function (state) {
            var selectionChanged = util_1.getter(_this.dataItem, _this.valueField) !== util_1.getter(state.dataItem, _this.valueField);
            _this.dataItem = state.dataItem;
            _this.value = state.value;
            _this.text = state.text;
            _this.loading = false;
            _this.disabled = false;
            _this.clearFilter();
            if (state.custom) {
                _this.selectionService.focused = -1;
            }
            if (selectionChanged) {
                var selectionArgs = state.custom ? undefined : _this.dataItem;
                _this.selectionChange.emit(selectionArgs);
            }
            _this.emitValueChange();
        });
    };
    ComboBoxComponent.prototype.attachStreams = function () {
        var _this = this;
        if (!kendo_angular_common_1.isDocumentAvailable()) {
            return;
        }
        this.subs.add(this.localization
            .changes.subscribe(function (_a) {
            var rtl = _a.rtl;
            _this.direction = rtl ? 'rtl' : 'ltr';
            _this.cdr.detectChanges();
        }));
        this.subs.add(rxjs_1.merge(this.navigationService.up, this.navigationService.down, this.navigationService.home, this.navigationService.end)
            .pipe(operators_1.filter(function (event) { return util_1.isPresent(event.index); }))
            .subscribe(function (event) { return _this.navigate(event.index); }));
        this.subs.add(this.navigationService.open.subscribe(this.handleNavigationOpen.bind(this)));
        this.subs.add(this.navigationService.close.subscribe(function () { return _this.togglePopup(false); }));
        this.subs.add(this.navigationService.esc.subscribe(this.handleEscape.bind(this)));
        this.subs.add(this.navigationService.enter.pipe(operators_1.tap(function (event) {
            if (_this.isOpen) {
                event.originalEvent.preventDefault();
            }
        }))
            .subscribe(this.handleEnter.bind(this)));
        this.subs.add(rxjs_1.merge(this.selectionService.onChange, this.selectionService.onSelect.pipe(operators_1.filter(function (_) { return !_this.isOpen; })))
            .pipe(operators_1.tap(function (_) {
            _this._filtering = false;
            _this.togglePopup(false);
        }), operators_1.map(function (event) { return _this.dataService.itemAt(event.indices[0]); }))
            .subscribe(function (dataItem) {
            _this.change(dataItem);
        }));
        this.subs.add(this.selectionService.onSelect.pipe(operators_1.filter(function (_) { return _this.isOpen; }), operators_1.tap(function (_) { return _this._filtering = false; }), operators_1.map(function (event) { return _this.dataService.itemAt(event.indices[0]); }))
            .subscribe(function (dataItem) {
            var selectionChanged = util_1.getter(dataItem, _this.valueField) !== util_1.getter(_this.dataItem, _this.valueField);
            _this.updateState({ dataItem: dataItem });
            if (selectionChanged) {
                _this.selectionChange.emit(dataItem);
            }
        }));
    };
    ComboBoxComponent.prototype.ngOnDestroy = function () {
        this.destroyPopup();
        this.subs.unsubscribe();
        if (util_1.isPresent(this.valueSubscription)) {
            this.valueSubscription.unsubscribe();
        }
        if (this.touchstartDisposeHandler) {
            this.touchstartDisposeHandler();
        }
        if (this.selectClickDisposeHandler) {
            this.selectClickDisposeHandler();
        }
    };
    ComboBoxComponent.prototype.ngOnChanges = function (changes) {
        var virtual = this.virtual;
        var requestInitialData = virtual && changes.data && changes.data.isFirstChange();
        if (requestInitialData) {
            this.pageChange({ skip: 0, take: virtual.pageSize });
        }
        if (kendo_angular_common_1.isChanged('valueNormalizer', changes)) {
            this.createValueStream();
        }
        if (kendo_angular_common_1.anyChanged(['textField', 'valueField', 'valuePrimitive'], changes, false)) {
            this.setState();
        }
    };
    ComboBoxComponent.prototype.ngAfterContentChecked = function () {
        this.verifySettings();
    };
    /**
     * Focuses a specific item of the ComboBox based on a provided index.
     * If null or invalid index is provided the focus will be removed.
     */
    ComboBoxComponent.prototype.focusItemAt = function (index) {
        var isInRange = index >= 0 && index < this.data.length;
        if (util_1.isPresent(index) && isInRange && !this.disabledItemsService.isIndexDisabled(index)) {
            this.selectionService.focus(index);
        }
        else {
            this.selectionService.focus(-1);
        }
    };
    /**
     * Focuses the ComboBox.
     */
    ComboBoxComponent.prototype.focus = function () {
        if (!this.disabled) {
            this.searchbar.focus();
        }
    };
    /**
     * Blurs the ComboBox.
     */
    ComboBoxComponent.prototype.blur = function () {
        if (!this.disabled) {
            this.searchbar.blur();
        }
    };
    /**
     * Toggles the visibility of the popup. If you use the `toggle` method to open or close the popup,
     * the `open` and `close` events will not be fired.
     *
     * @param open - The state of the popup.
     */
    ComboBoxComponent.prototype.toggle = function (open) {
        var _this = this;
        Promise.resolve(null).then(function () {
            var shouldOpen = util_1.isPresent(open) ? open : !_this._open;
            _this._toggle(shouldOpen);
            _this.cdr.markForCheck();
        });
    };
    Object.defineProperty(ComboBoxComponent.prototype, "isOpen", {
        /**
         * Returns the current open state of the popup.
         */
        get: function () {
            return this._open;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Resets the value of the ComboBox.
     * If you use the `reset` method to clear the value of the component,
     * the model will not update automatically and the `selectionChange` and `valueChange` events will not be fired.
     */
    ComboBoxComponent.prototype.reset = function () {
        this.value = undefined;
        this.clearState();
        this.resetSelection();
    };
    /**
     * @hidden
     *
     * Used by the TextBoxContainer to determine if the floating label
     * should be rendered in the input when the component is not focused.
     */
    ComboBoxComponent.prototype.isEmpty = function () {
        var textEmpty = !util_1.isPresent(this.text) || util_1.isEmptyString(this.text);
        var valueEmpty = !util_1.isPresent(this.value) || util_1.isEmptyString(this.value);
        return textEmpty && valueEmpty;
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.messageFor = function (key) {
        return this.localization.get(key);
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.clearValue = function (event) {
        event.stopImmediatePropagation();
        this.focus();
        this._filtering = true;
        this._previousDataItem = undefined;
        this.selectionService.resetSelection([]);
        this.clearValueSubject.next();
        this._filtering = false;
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.writeValue = function (value) {
        this.value = value === null ? undefined : value;
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.registerOnChange = function (fn) {
        this.onChangeCallback = fn;
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.registerOnTouched = function (fn) {
        this.onTouchedCallback = fn;
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.setDisabledState = function (isDisabled) {
        this.disabled = isDisabled;
    };
    Object.defineProperty(ComboBoxComponent.prototype, "buttonClasses", {
        /**
         * @hidden
         */
        get: function () {
            return this.loading ? 'k-i-loading' : this.iconClass || 'k-i-arrow-s';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ComboBoxComponent.prototype, "selectButtonClasses", {
        /**
         * @hidden
         */
        get: function () {
            return (this.size ? util_1.getSizeClass('button', this.size) : '') + " " + (this.fillMode ? 'k-button-' + this.fillMode : '') + " " + (this.fillMode ? 'k-button-' + this.fillMode + '-base' : '') + "\n        ";
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.onResize = function () {
        if (this.isOpen) {
            var popupWrapper = this.popupRef.popupElement;
            var _a = this.popupWidth, min = _a.min, max = _a.max;
            popupWrapper.style.minWidth = min;
            popupWrapper.style.width = max;
        }
    };
    ComboBoxComponent.prototype.verifySettings = function () {
        if (!core_1.isDevMode()) {
            return;
        }
        if (this.valuePrimitive === true && util_1.isPresent(this.value) && typeof this.value === "object") {
            throw new Error(error_messages_1.ComboBoxMessages.primitive);
        }
        if (this.valuePrimitive === false && util_1.isPresent(this.value) && typeof this.value !== "object") {
            throw new Error(error_messages_1.ComboBoxMessages.object);
        }
        var valueOrText = !util_1.isPresent(this.valueField) !== !util_1.isPresent(this.textField);
        if (valueOrText) {
            throw new Error(error_messages_1.ComboBoxMessages.textAndValue);
        }
        if (this.virtual && isNaN(this.virtual.itemHeight)) {
            throw new Error(error_messages_1.ComboBoxMessages.noItemHeight);
        }
    };
    ComboBoxComponent.prototype.setState = function () {
        // Filtering in process, do nothing.
        if (this._filtering) {
            return;
        }
        var value = this.value;
        var valueField = this.valueField;
        var resolved = this.findDataItem({ valueField: valueField, value: value });
        if (util_1.isPresent(resolved.index) && resolved.index !== -1) {
            this.updateState({ dataItem: resolved.dataItem, confirm: true });
            this.resetSelection(resolved.index);
        }
        else if (util_1.isPresent(value) && this.allowCustom) {
            this.updateState({ dataItem: value });
            this.resetSelection(-1);
        }
        else if (this._previousDataItem && this.value) {
            this.updateState({ dataItem: this._previousDataItem });
            this.resetSelection();
        }
        else {
            this.clearState();
            this.resetSelection(-1);
        }
    };
    ComboBoxComponent.prototype.updateState = function (_a) {
        var dataItem = _a.dataItem, _b = _a.confirm, confirm = _b === void 0 ? false : _b;
        this.dataItem = dataItem;
        this.text = util_1.getter(dataItem, this.textField);
        if (confirm) {
            this._previousDataItem = dataItem;
        }
    };
    ComboBoxComponent.prototype.clearState = function () {
        this.text = undefined;
        this.dataItem = undefined;
    };
    ComboBoxComponent.prototype.resetSelection = function (index) {
        var clear = !util_1.isPresent(index) || index < 0;
        this.selectionService.resetSelection(clear ? [] : [index]);
        this.selectionService.focused = index;
    };
    ComboBoxComponent.prototype.firstFocusableIndex = function (index) {
        var maxIndex = this.data.length - 1;
        if (this.disabledItemsService.isIndexDisabled(index)) {
            return (index < maxIndex) ? this.firstFocusableIndex(index + 1) : undefined;
        }
        else {
            return index;
        }
    };
    ComboBoxComponent.prototype.findIndexPredicate = function (text) {
        var _this = this;
        if (this.dataService.grouped) {
            return function (item) {
                var itemText = util_1.getter(item.value, _this.textField);
                itemText = !util_1.isPresent(itemText) ? "" : itemText.toString().toLowerCase();
                return itemText.startsWith(text.toLowerCase());
            };
        }
        else {
            return function (item) {
                var itemText = util_1.getter(item, _this.textField);
                itemText = !util_1.isPresent(itemText) ? "" : itemText.toString().toLowerCase();
                return itemText.startsWith(text.toLowerCase());
            };
        }
    };
    ComboBoxComponent.prototype.findDataItem = function (_a) {
        var _this = this;
        var valueField = _a.valueField, value = _a.value;
        var result = {
            dataItem: null,
            index: -1
        };
        var comparer = function (element) {
            var dataItem = _this.dataService.grouped ? element.value : element;
            return util_1.getter(dataItem, valueField) === util_1.getter(value, valueField);
        };
        var index = this.dataService.findIndex(comparer);
        result.dataItem = this.dataService.itemAt(index);
        result.index = index;
        return result;
    };
    ComboBoxComponent.prototype.search = function (text, startFrom) {
        if (startFrom === void 0) { startFrom = 0; }
        var index;
        if (text.length && this.dataService.itemsCount) {
            index = this.dataService.findIndex(this.findIndexPredicate(text), startFrom);
        }
        else {
            index = -1;
        }
        if (this.disabledItemsService.isIndexDisabled(index)) {
            if (index + 1 < this.dataService.itemsCount) {
                this.search(text, index + 1);
            }
            else {
                this.selectionService.focus(-1);
            }
        }
        else {
            this.selectionService.focus(index);
            if (this.suggest) {
                this.suggestedText = util_1.getter(this.dataService.itemAt(index), this.textField);
            }
        }
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.getSuggestion = function () {
        var hasSelected = !!this.selectionService.selected.length;
        var shouldSuggest = this.suggest && !this.backspacePressed && this.suggestedText && this.text;
        if (!hasSelected && shouldSuggest && this.suggestedText.toLowerCase().startsWith(this.text.toLowerCase())) {
            return this.suggestedText;
        }
        else {
            this.suggestedText = undefined;
        }
    };
    ComboBoxComponent.prototype.navigate = function (index) {
        if (this.dataService.itemsCount === 0) {
            return;
        }
        this.text = util_1.getter(this.dataService.itemAt(index), this.textField);
        this.selectionService.select(index);
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.handleNavigate = function (event) {
        var hasSelected = util_1.isPresent(this.selectionService.selected[0]);
        var focused = isNaN(this.selectionService.focused) ? this.firstFocusableIndex(0) : this.selectionService.focused;
        var offset = 0;
        if (this.disabled || this.readonly) {
            return;
        }
        if (event.keyCode === kendo_angular_common_2.Keys.Home || event.keyCode === kendo_angular_common_2.Keys.End) {
            return;
        }
        if (!hasSelected) {
            if (event.keyCode === kendo_angular_common_2.Keys.ArrowDown) {
                offset = -1;
            }
            else if (event.keyCode === kendo_angular_common_2.Keys.ArrowUp) {
                offset = 1;
            }
        }
        var action = this.navigationService.process({
            current: offset + focused,
            max: this.dataService.itemsCount - 1,
            min: 0,
            originalEvent: event
        });
        if (action !== navigation_action_1.NavigationAction.Undefined &&
            action !== navigation_action_1.NavigationAction.Left &&
            action !== navigation_action_1.NavigationAction.Right &&
            action !== navigation_action_1.NavigationAction.Backspace &&
            action !== navigation_action_1.NavigationAction.Delete &&
            ((action === navigation_action_1.NavigationAction.Enter && this.isOpen) || action !== navigation_action_1.NavigationAction.Enter)) {
            event.preventDefault();
        }
    };
    ComboBoxComponent.prototype.handleEnter = function () {
        var text = this.text;
        var focused = this.selectionService.focused;
        var hasFocused = util_1.isPresent(focused) && focused !== -1;
        var previousText = util_1.getter(this._previousDataItem, this.textField) || "";
        var focusedItemText = util_1.getter(this.dataService.itemAt(focused), this.textField);
        var textHasChanged = text !== previousText;
        this.togglePopup(false);
        this._filtering = false;
        if (this.allowCustom && textHasChanged) {
            if (text === focusedItemText || this.useSuggestion()) {
                this.selectionService.change(focused);
            }
            else {
                this.change(text, true);
            }
        }
        if (!this.allowCustom) {
            if (hasFocused) {
                this.selectionService.change(focused);
            }
            else if (textHasChanged) {
                this.change(text, true);
            }
        }
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.handleBlur = function () {
        var _this = this;
        this._filtering = false;
        this.searchbar.input.nativeElement.scrollLeft = 0; // Firefox doesn't auto-scroll to the left on blur like other browsers
        this.isFocused = false;
        var unresolvedSelection = util_1.getter(this.dataItem, this.valueField) !== util_1.getter(this.value, this.valueField);
        var currentText = this.searchbar.value;
        var textHasChanged = currentText !== (util_1.getter(this.dataItem, this.textField) || '');
        var valueHasChanged = unresolvedSelection || textHasChanged;
        var runInZone = valueHasChanged || kendo_angular_common_1.hasObservers(this.onBlur) || kendo_angular_common_1.hasObservers(this.close) || util_1.isUntouched(this.wrapper.nativeElement);
        if (runInZone) {
            this.zone.run(function () {
                if (valueHasChanged) {
                    var lowerCaseMatch = util_1.isPresent(_this.focusedItemText) && _this.focusedItemText.toLowerCase() === currentText.toLowerCase();
                    if (lowerCaseMatch || unresolvedSelection) {
                        _this.selectionService.change(_this.selectionService.focused);
                    }
                    else {
                        _this.change(currentText, true);
                    }
                }
                _this.onBlur.emit();
                _this.onTouchedCallback();
                _this.togglePopup(false);
            });
        }
        else {
            this.togglePopup(false);
        }
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.handleEscape = function () {
        this.togglePopup(false);
        // clear the focus only if the focused item is not selected
        var hasSelected = this.selectionService.selected.length > 0;
        if (!hasSelected) {
            this.suggestedText = null;
            this.selectionService.focused = -1;
        }
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.handleNavigationOpen = function () {
        this.restoreItemFocus();
        this.togglePopup(true);
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.searchBarChange = function (text) {
        var currentTextLength = this.text ? this.text.length : 0;
        this.backspacePressed = (text.length < currentTextLength) ? true : false;
        this.text = text;
        // Reset the selection prior to filter. If a match is present, it will be resolved. If a match is not present, it is not needed.
        this.selectionService.resetSelection([]);
        this.togglePopup(true);
        this._filtering = true;
        if (this.filterable && this.filterText !== text) {
            this.filterText = text;
            this.filterChange.emit(text);
        }
        else {
            this.search(text);
        }
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.handleFocus = function () {
        var _this = this;
        this.isFocused = true;
        if (kendo_angular_common_1.hasObservers(this.onFocus)) {
            this.zone.run(function () { return _this.onFocus.emit(); });
        }
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.pageChange = function (event) {
        var virtual = this.virtual;
        virtual.skip = event.skip;
    };
    ComboBoxComponent.prototype.change = function (candidate, isCustom) {
        if (isCustom === void 0) { isCustom = false; }
        if (isCustom) {
            this.customValueSubject.next(candidate);
        }
        else {
            this.valueSubject.next(candidate);
        }
    };
    ComboBoxComponent.prototype.emitValueChange = function () {
        this.onChangeCallback(this.value);
        this.valueChange.emit(this.value);
        this._previousDataItem = this.dataItem;
    };
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.selectClick = function () {
        if (!this.touchEnabled) {
            this.searchbar.focus();
        }
        if (!this.isOpen) {
            this.restoreItemFocus();
        }
        this.togglePopup(!this.isOpen);
    };
    Object.defineProperty(ComboBoxComponent.prototype, "listContainerClasses", {
        get: function () {
            return ['k-list-container', 'k-reset'].concat(this.popupSettings.popupClass || []);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     */
    ComboBoxComponent.prototype.preventEventDefault = function (event) {
        event.preventDefault();
    };
    Object.defineProperty(ComboBoxComponent.prototype, "focusedItemText", {
        get: function () {
            var focused = this.selectionService.focused;
            if (!util_1.isPresent(focused) || focused === -1) {
                return null;
            }
            var itemText = util_1.getter(this.dataService.itemAt(focused), this.textField);
            return !util_1.isPresent(itemText) ? "" : itemText.toString();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Focuses the first match when there's text in the input field, but no focused item.
     */
    ComboBoxComponent.prototype.restoreItemFocus = function () {
        var hasFocus = util_1.isPresent(this.selectionService.focused) && this.selectionService.focused > -1;
        if (!hasFocus && this.text && this.dataService.itemsCount) {
            if (this.filterable) {
                this.selectionService.focused = this.firstFocusableIndex(0);
            }
            else {
                this.search(this.text);
            }
        }
    };
    ComboBoxComponent.prototype.useSuggestion = function () {
        if (!(this.suggest && util_1.isPresent(this.searchbar.value))) {
            return false;
        }
        var focusedDataItem = this.dataService.itemAt(this.selectionService.focused);
        var focusedItemText = util_1.getter(focusedDataItem, this.textField);
        if (!util_1.isPresent(focusedItemText)) {
            return false;
        }
        return this.searchbar.value.toLowerCase() === focusedItemText.toLowerCase();
    };
    ComboBoxComponent.prototype.destroyPopup = function () {
        if (this.popupRef) {
            this.popupRef.popupElement
                .removeEventListener('mousedown', this.popupMouseDownHandler);
            this.popupRef.close();
            this.popupRef = null;
        }
    };
    ComboBoxComponent.prototype.createPopup = function () {
        var _this = this;
        if (this.virtual) {
            this.virtual.skip = 0;
        }
        var horizontalAlign = this.direction === "rtl" ? "right" : "left";
        var anchorPosition = { horizontal: horizontalAlign, vertical: "bottom" };
        var popupPosition = { horizontal: horizontalAlign, vertical: "top" };
        this.popupRef = this.popupService.open({
            anchor: this.wrapper,
            animate: this.popupSettings.animate,
            appendTo: this.appendTo,
            content: this.popupTemplate,
            popupClass: this.listContainerClasses,
            positionMode: 'absolute',
            anchorAlign: anchorPosition,
            popupAlign: popupPosition
        });
        var popupWrapper = this.popupRef.popupElement;
        var _a = this.popupWidth, min = _a.min, max = _a.max;
        popupWrapper.addEventListener('mousedown', this.popupMouseDownHandler);
        popupWrapper.style.minWidth = min;
        popupWrapper.style.width = max;
        popupWrapper.style.height = this.popupHeight;
        popupWrapper.setAttribute("dir", this.direction);
        this.popupRef.popupOpen.subscribe(function () {
            _this.cdr.detectChanges();
            _this.optionsList.scrollToItem(_this.selectionService.focused);
            _this.opened.emit();
        });
        this.popupRef.popupClose.subscribe(function () {
            _this.closed.emit();
        });
        this.popupRef.popupAnchorViewportLeave.subscribe(function () { return _this.togglePopup(false); });
    };
    ComboBoxComponent.prototype._toggle = function (open) {
        this._open = open;
        this.destroyPopup();
        if (this._open) {
            this.createPopup();
        }
    };
    ComboBoxComponent.prototype.triggerPopupEvents = function (open) {
        var eventArgs = new preventable_event_1.PreventableEvent();
        if (open) {
            this.open.emit(eventArgs);
        }
        else {
            this.close.emit(eventArgs);
        }
        return eventArgs.isDefaultPrevented();
    };
    ComboBoxComponent.prototype.clearFilter = function () {
        if (!(this.filterable && this.filterText)) {
            return;
        }
        this.filterText = '';
        this.filterChange.emit(this.filterText);
    };
    ComboBoxComponent.prototype.subscribeTouchEvents = function () {
        var _this = this;
        if (!kendo_angular_common_1.isDocumentAvailable() || !this.touchEnabled) {
            return;
        }
        this.zone.runOutsideAngular(function () {
            // Roll up ComboBox on iOS when tapped outside
            return _this.touchstartDisposeHandler = _this.renderer.listen(document, 'touchstart', function (e) {
                var target = e.target;
                var isInDropDown = util_1.inDropDown(_this.wrapper, target, _this.popupRef);
                if (_this.isFocused && !isInDropDown) {
                    // Close popup and mobile keyboard if searchbar is focused
                    _this.zone.run(function () { return _this.blur(); });
                }
                else if (_this.isOpen && !isInDropDown) {
                    // Close popup if the popup is opened via the select click
                    _this.zone.run(function () { return _this.togglePopup(false); });
                }
            });
        });
    };
    ComboBoxComponent.prototype.attachSelectClickHandler = function () {
        var selectElement = this.select.nativeElement;
        var event = kendo_common_1.pointers ? 'pointerdown' : 'click';
        this.selectClickDisposeHandler = this.renderer.listen(selectElement, event, this.selectClick.bind(this));
    };
    ComboBoxComponent.prototype.setComponentClasses = function () {
        if (this.size) {
            this.renderer.addClass(this.wrapper.nativeElement, util_1.getSizeClass('input', this.size));
        }
        if (this.rounded) {
            this.renderer.addClass(this.wrapper.nativeElement, util_1.getRoundedClass(this.rounded));
        }
        if (this.fillMode) {
            this.renderer.addClass(this.wrapper.nativeElement, util_1.getFillModeClass('input', this.fillMode));
        }
    };
    var ComboBoxComponent_1;
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], ComboBoxComponent.prototype, "focusableId", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], ComboBoxComponent.prototype, "allowCustom", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object),
        tslib_1.__metadata("design:paramtypes", [Object])
    ], ComboBoxComponent.prototype, "data", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object),
        tslib_1.__metadata("design:paramtypes", [Object])
    ], ComboBoxComponent.prototype, "value", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], ComboBoxComponent.prototype, "textField", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], ComboBoxComponent.prototype, "valueField", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean),
        tslib_1.__metadata("design:paramtypes", [Boolean])
    ], ComboBoxComponent.prototype, "valuePrimitive", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Function)
    ], ComboBoxComponent.prototype, "valueNormalizer", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], ComboBoxComponent.prototype, "placeholder", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object),
        tslib_1.__metadata("design:paramtypes", [Object])
    ], ComboBoxComponent.prototype, "popupSettings", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Number)
    ], ComboBoxComponent.prototype, "listHeight", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String)
    ], ComboBoxComponent.prototype, "iconClass", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], ComboBoxComponent.prototype, "loading", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], ComboBoxComponent.prototype, "suggest", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], ComboBoxComponent.prototype, "clearButton", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], ComboBoxComponent.prototype, "disabled", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Function),
        tslib_1.__metadata("design:paramtypes", [Function])
    ], ComboBoxComponent.prototype, "itemDisabled", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], ComboBoxComponent.prototype, "readonly", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Number)
    ], ComboBoxComponent.prototype, "tabindex", void 0);
    tslib_1.__decorate([
        core_1.Input("tabIndex"),
        tslib_1.__metadata("design:type", Number),
        tslib_1.__metadata("design:paramtypes", [Number])
    ], ComboBoxComponent.prototype, "tabIndex", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Boolean)
    ], ComboBoxComponent.prototype, "filterable", void 0);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", Object),
        tslib_1.__metadata("design:paramtypes", [Object])
    ], ComboBoxComponent.prototype, "virtual", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String),
        tslib_1.__metadata("design:paramtypes", [String])
    ], ComboBoxComponent.prototype, "size", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String),
        tslib_1.__metadata("design:paramtypes", [String])
    ], ComboBoxComponent.prototype, "rounded", null);
    tslib_1.__decorate([
        core_1.Input(),
        tslib_1.__metadata("design:type", String),
        tslib_1.__metadata("design:paramtypes", [String])
    ], ComboBoxComponent.prototype, "fillMode", null);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], ComboBoxComponent.prototype, "valueChange", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], ComboBoxComponent.prototype, "selectionChange", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], ComboBoxComponent.prototype, "filterChange", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], ComboBoxComponent.prototype, "open", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], ComboBoxComponent.prototype, "opened", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], ComboBoxComponent.prototype, "close", void 0);
    tslib_1.__decorate([
        core_1.Output(),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], ComboBoxComponent.prototype, "closed", void 0);
    tslib_1.__decorate([
        core_1.Output('focus'),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], ComboBoxComponent.prototype, "onFocus", void 0);
    tslib_1.__decorate([
        core_1.Output('blur'),
        tslib_1.__metadata("design:type", core_1.EventEmitter)
    ], ComboBoxComponent.prototype, "onBlur", void 0);
    tslib_1.__decorate([
        core_1.ContentChild(item_template_directive_1.ItemTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", item_template_directive_1.ItemTemplateDirective)
    ], ComboBoxComponent.prototype, "template", void 0);
    tslib_1.__decorate([
        core_1.ContentChild(header_template_directive_1.HeaderTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", header_template_directive_1.HeaderTemplateDirective)
    ], ComboBoxComponent.prototype, "headerTemplate", void 0);
    tslib_1.__decorate([
        core_1.ContentChild(footer_template_directive_1.FooterTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", footer_template_directive_1.FooterTemplateDirective)
    ], ComboBoxComponent.prototype, "footerTemplate", void 0);
    tslib_1.__decorate([
        core_1.ContentChild(no_data_template_directive_1.NoDataTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", no_data_template_directive_1.NoDataTemplateDirective)
    ], ComboBoxComponent.prototype, "noDataTemplate", void 0);
    tslib_1.__decorate([
        core_1.ContentChild(group_template_directive_1.GroupTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", group_template_directive_1.GroupTemplateDirective)
    ], ComboBoxComponent.prototype, "groupTemplate", void 0);
    tslib_1.__decorate([
        core_1.ContentChild(fixed_group_template_directive_1.FixedGroupTemplateDirective, { static: false }),
        tslib_1.__metadata("design:type", fixed_group_template_directive_1.FixedGroupTemplateDirective)
    ], ComboBoxComponent.prototype, "fixedGroupTemplate", void 0);
    tslib_1.__decorate([
        core_1.ViewChild('container', { read: core_1.ViewContainerRef, static: true }),
        tslib_1.__metadata("design:type", core_1.ViewContainerRef)
    ], ComboBoxComponent.prototype, "container", void 0);
    tslib_1.__decorate([
        core_1.ViewChild('popupTemplate', { static: true }),
        tslib_1.__metadata("design:type", core_1.TemplateRef)
    ], ComboBoxComponent.prototype, "popupTemplate", void 0);
    tslib_1.__decorate([
        core_1.ViewChild(searchbar_component_1.SearchBarComponent, { static: true }),
        tslib_1.__metadata("design:type", searchbar_component_1.SearchBarComponent)
    ], ComboBoxComponent.prototype, "searchbar", void 0);
    tslib_1.__decorate([
        core_1.ViewChild('optionsList', { static: false }),
        tslib_1.__metadata("design:type", list_component_1.ListComponent)
    ], ComboBoxComponent.prototype, "optionsList", void 0);
    tslib_1.__decorate([
        core_1.ViewChild('select', { static: true }),
        tslib_1.__metadata("design:type", core_1.ElementRef)
    ], ComboBoxComponent.prototype, "select", void 0);
    tslib_1.__decorate([
        core_1.HostBinding('class.k-combobox'),
        core_1.HostBinding('class.k-input'),
        tslib_1.__metadata("design:type", Boolean)
    ], ComboBoxComponent.prototype, "widgetClasses", void 0);
    tslib_1.__decorate([
        core_1.HostBinding('class.k-disabled'),
        tslib_1.__metadata("design:type", Boolean),
        tslib_1.__metadata("design:paramtypes", [])
    ], ComboBoxComponent.prototype, "isDisabled", null);
    tslib_1.__decorate([
        core_1.HostBinding('class.k-loading'),
        tslib_1.__metadata("design:type", Boolean),
        tslib_1.__metadata("design:paramtypes", [])
    ], ComboBoxComponent.prototype, "isLoading", null);
    tslib_1.__decorate([
        core_1.HostBinding('attr.dir'),
        tslib_1.__metadata("design:type", Object),
        tslib_1.__metadata("design:paramtypes", [])
    ], ComboBoxComponent.prototype, "dir", null);
    ComboBoxComponent = ComboBoxComponent_1 = tslib_1.__decorate([
        core_1.Component({
            exportAs: 'kendoComboBox',
            providers: [
                exports.COMBOBOX_VALUE_ACCESSOR,
                data_service_1.DataService,
                selection_service_1.SelectionService,
                navigation_service_1.NavigationService,
                disabled_items_service_1.DisabledItemsService,
                kendo_angular_l10n_1.LocalizationService,
                {
                    provide: kendo_angular_l10n_1.L10N_PREFIX,
                    useValue: 'kendo.combobox'
                },
                {
                    provide: filterable_component_1.FilterableComponent, useExisting: core_1.forwardRef(function () { return ComboBoxComponent_1; })
                },
                {
                    provide: kendo_angular_common_1.KendoInput, useExisting: core_1.forwardRef(function () { return ComboBoxComponent_1; })
                }
            ],
            selector: 'kendo-combobox',
            template: "\n        <ng-container kendoComboBoxLocalizedMessages\n            i18n-noDataText=\"kendo.combobox.noDataText|The text displayed in the popup when there are no items\"\n            noDataText=\"NO DATA FOUND\"\n\n            i18n-clearTitle=\"kendo.combobox.clearTitle|The title of the clear button\"\n            clearTitle=\"clear\"\n\n            i18n-selectButtonText=\"kendo.combobox.selectButtonText|The text set as aria-label on the select button\"\n            selectButtonText=\"Select\"\n        >\n        </ng-container>\n        <kendo-searchbar #searchbar\n            [role]=\"'combobox'\"\n            [id]=\"focusableId\"\n            [listId]=\"listBoxId\"\n            [activeDescendant]=\"activeDescendant\"\n            [noDataLabel]=\"noDataLabel\"\n            [userInput]=\"text\"\n            [suggestedText]=\"getSuggestion()\"\n            [disabled]=\"disabled\"\n            [readonly]=\"readonly\"\n            [tabIndex]=\"tabIndex\"\n            [popupOpen]=\"isOpen\"\n            [placeholder]=\"placeholder\"\n            (onNavigate)=\"handleNavigate($event)\"\n            (valueChange)=\"searchBarChange($event)\"\n            (onBlur)=\"handleBlur()\"\n            (onFocus)=\"handleFocus()\"\n        ></kendo-searchbar>\n        <span\n            *ngIf=\"clearButton && !loading && !disabled && !readonly && text?.length\"\n            class=\"k-clear-value\"\n            [style.visibility]=\"clearButtonVisiblity\"\n            aria-hidden=\"true\"\n            [attr.title]=\"messageFor('clearTitle')\"\n            (click)=\"clearValue($event)\"\n            [kendoEventsOutsideAngular]=\"{\n                mousedown: preventEventDefault\n            }\"\n        >\n            <span class=\"k-icon k-i-x\"></span>\n        </span>\n        <button\n            #select\n            unselectable=\"on\"\n            type=\"button\"\n            class=\"k-input-button k-button k-icon-button\"\n            [ngClass]=\"selectButtonClasses\"\n            [attr.aria-label]=\"messageFor('selectButtonText')\"\n            [kendoEventsOutsideAngular]=\"{\n                mousedown: preventEventDefault\n            }\"\n        >\n            <span class=\"k-button-icon k-icon\" [ngClass]=\"buttonClasses\"></span>\n        </button>\n        <ng-template #popupTemplate>\n            <!--header template-->\n            <ng-template *ngIf=\"headerTemplate\"\n                [templateContext]=\"{\n                    templateRef: headerTemplate.templateRef\n                }\">\n            </ng-template>\n            <!--list-->\n            <kendo-list\n                #optionsList\n                [size]=\"size\"\n                [rounded]=\"rounded\"\n                [id]=\"listBoxId\"\n                [optionPrefix]=\"optionPrefix\"\n                [data]=\"data\"\n                [textField]=\"textField\"\n                [valueField]=\"valueField\"\n                [template]=\"template\"\n                [groupTemplate]=\"groupTemplate\"\n                [fixedGroupTemplate]=\"fixedGroupTemplate\"\n                [height]=\"listHeight\"\n                [show]=\"isOpen\"\n                [virtual]=\"virtual\"\n                (pageChange)=\"pageChange($event)\"\n            >\n            </kendo-list>\n            <!--no-data template-->\n            <div class=\"k-no-data\" *ngIf=\"data.length === 0\">\n                <ng-template [ngIf]=\"noDataTemplate\"\n                    [templateContext]=\"{\n                        templateRef: noDataTemplate ? noDataTemplate.templateRef : undefined\n                    }\">\n                </ng-template>\n                <ng-template [ngIf]=\"!noDataTemplate\">\n                    <div>{{ messageFor('noDataText') }}</div>\n                </ng-template>\n            </div>\n            <!--footer template-->\n            <ng-template *ngIf=\"footerTemplate\"\n                [templateContext]=\"{\n                    templateRef: footerTemplate.templateRef\n                }\">\n            </ng-template>\n        </ng-template>\n        <ng-template [ngIf]=\"isOpen\">\n            <kendo-resize-sensor (resize)=\"onResize()\"></kendo-resize-sensor>\n        </ng-template>\n        <ng-container #container></ng-container>\n  "
        }),
        tslib_1.__param(10, core_1.Optional()), tslib_1.__param(10, core_1.Inject(touch_enabled_1.TOUCH_ENABLED)),
        tslib_1.__metadata("design:paramtypes", [core_1.ElementRef,
            kendo_angular_l10n_1.LocalizationService,
            kendo_angular_popup_1.PopupService,
            selection_service_1.SelectionService,
            navigation_service_1.NavigationService,
            disabled_items_service_1.DisabledItemsService,
            data_service_1.DataService,
            core_1.NgZone,
            core_1.ChangeDetectorRef,
            core_1.Renderer2, Boolean])
    ], ComboBoxComponent);
    return ComboBoxComponent;
}());
exports.ComboBoxComponent = ComboBoxComponent;
